import { getRefresh, parseJwt, refreshAccessToken } from './auth.js';

// ─────────────────────────────────────────────────────────────────────────────
// Session lifecycle — the part of authentication the student experiences.
// core.md §15; ported from the dentist client's src/lib/session.js.
//
// The mechanics (PKCE, tokens, refresh) live in auth.js. What this module adds
// is the answer to a question auth.js never asked: what happens to the WORK IN
// PROGRESS when a session ends? A full-document redirect from inside the fetch
// wrapper unmounts React and takes a half-written SOAP note, a composer draft
// and the onboarding form with it. So a session ending is an EVENT, not a
// navigation:
//
//   expiring — the refresh token is close to its end; raised early so the
//              session can be extended silently.
//   expired  — it is over. The app STAYS MOUNTED and raises a dialog. The
//              re-login is something the student chooses, and it returns them
//              to the same route via sessionStorage.return_to.
//
// Timing is driven by the REFRESH token, not the access token: bffClient renews
// the access token silently whenever it is stale.
// ─────────────────────────────────────────────────────────────────────────────

export const SessionEvent = {
  EXPIRING: 'expiring',
  EXPIRED: 'expired',
  // A request came back `NOT_ONBOARDED` mid-session: the profile this app
  // booted on is gone (deleted from another tab, or a store restored from
  // before it existed). Not a session event in the token sense — the token is
  // fine — but it rides the same bus because it has the same shape: something
  // outside the router has to take over, and the fetch wrapper must not
  // navigate. App.jsx re-runs the boot query and lets the onboarding screen
  // replace the router (core.md §4.4). `SessionDialog` ignores it.
  NOT_ONBOARDED: 'not_onboarded',
};

/** The two events that are about the TOKEN, which is what the dialog is for. */
export const isTokenEvent = (event) =>
  event === SessionEvent.EXPIRING || event === SessionEvent.EXPIRED;

/** How long before the end to raise the warning. */
export const EXPIRY_WARNING_MS = 120_000;

// setTimeout wraps above 2^31-1 ms and fires IMMEDIATELY; a Keycloak SSO session
// can be days long.
const MAX_TIMEOUT_MS = 2_147_483_647;

const listeners = new Set();

export function onSessionEvent(listener) {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

/** Never throws — called from catch blocks and timers. */
export function emitSession(event) {
  for (const listener of listeners) {
    try { listener(event); } catch { /* one broken subscriber must not stop the rest */ }
  }
}

/** Test seam. */
export function resetSessionListeners() { listeners.clear(); }

// ── Returning the student to where they were ─────────────────────────────

const RETURN_KEY = 'return_to';

export function rememberReturnPath(path) {
  const value = path ?? (typeof window !== 'undefined' ? `${window.location.pathname}${window.location.search}` : '');
  try {
    // Only in-app paths — an absolute URL would be a redirect parameter.
    if (typeof value === 'string' && value.startsWith('/') && !value.startsWith('//')) {
      sessionStorage.setItem(RETURN_KEY, value);
    }
  } catch { /* storage off */ }
}

/** Read and clear the stored destination. One-shot. */
export function takeReturnPath() {
  try {
    const path = sessionStorage.getItem(RETURN_KEY);
    sessionStorage.removeItem(RETURN_KEY);
    return path && path.startsWith('/') && !path.startsWith('//') ? path : null;
  } catch {
    return null;
  }
}

// ── Expiry scheduling ────────────────────────────────────────────────────

export function refreshTokenExpiry() {
  const refresh = getRefresh();
  if (!refresh) return null;
  const payload = parseJwt(refresh);
  // Offline tokens carry exp: 0 = "does not expire".
  if (!payload?.exp) return null;
  return payload.exp * 1000;
}

export function watchSession({ emit = emitSession, now = () => Date.now() } = {}) {
  let warnTimer = null;
  let endTimer = null;
  let cancelled = false;

  const clear = () => { clearTimeout(warnTimer); clearTimeout(endTimer); warnTimer = null; endTimer = null; };

  const arm = () => {
    clear();
    if (cancelled) return;
    const expiry = refreshTokenExpiry();
    if (!expiry) return;
    const remaining = expiry - now();
    if (remaining <= 0) { emit(SessionEvent.EXPIRED); return; }
    const untilWarning = remaining - EXPIRY_WARNING_MS;
    if (untilWarning > 0) warnTimer = setTimeout(() => emit(SessionEvent.EXPIRING), Math.min(untilWarning, MAX_TIMEOUT_MS));
    else emit(SessionEvent.EXPIRING);
    endTimer = setTimeout(() => emit(SessionEvent.EXPIRED), Math.min(remaining, MAX_TIMEOUT_MS));
  };

  arm();
  return { rearm: arm, cancel: () => { cancelled = true; clear(); } };
}

/** «ادامهٔ کار» — renew silently. True when the session is good again. */
export async function extendSession() {
  const ok = await refreshAccessToken().catch(() => false);
  return Boolean(ok);
}
